import { create } from 'zustand';
import { DEFAULT_FPS, INITIAL_PX_PER_MS } from '../constants';
import type { EditorProject, Track, Clip, MediaAsset, TimelineViewState, IngestStatus, EffectClip } from '../types';
import { withUndo, type UndoSlice } from './undoMiddleware';
import { zoomIn as zoomInLevel, zoomOut as zoomOutLevel, nearestZoomLevel } from '../lib/zoomLevels';

function uid() {
  return crypto.randomUUID();
}

function now() {
  return Date.now();
}

interface EditorStore extends UndoSlice {
  project: EditorProject;
  timeline: TimelineViewState;

  // Project actions
  setProject: (project: EditorProject) => void;
  resetProject: (name?: string) => void;

  // Track actions
  addTrack: (type: 'video' | 'audio', name?: string) => string;
  removeTrack: (trackId: string) => void;
  toggleTrackMute: (trackId: string) => void;
  toggleTrackLock: (trackId: string) => void;

  // Clip actions
  addClip: (trackId: string, clip: Omit<Clip, 'id' | 'trackId'>) => string;
  removeClip: (clipId: string) => void;
  moveClip: (clipId: string, newStartMs: number) => void;
  splitClip: (clipId: string, splitAtMs: number) => void;
  trimClip: (clipId: string, side: 'left' | 'right', deltaMs: number) => void;

  // Asset actions
  addAsset: (asset: MediaAsset) => void;
  updateAssetStatus: (assetId: string, status: IngestStatus, progress?: number, error?: string) => void;
  updateAsset: (assetId: string, updates: Partial<MediaAsset>) => void;
  removeAsset: (assetId: string) => void;

  // Project canvas
  setProjectAspect: (aspect: number) => void;

  // Per-track mixing (Phase 3)
  setTrackVolume: (trackId: string, volume: number) => void;
  setTrackPan: (trackId: string, pan: number) => void;
  toggleTrackSolo: (trackId: string) => void;

  // Clip fades (Phase 4)
  setClipFadeIn: (clipId: string, durationMs: number, type?: import('../types').FadeType) => void;
  setClipFadeOut: (clipId: string, durationMs: number, type?: import('../types').FadeType) => void;

  // Effect Clips (Region-based Event FX)
  addEffectTrack: (parentTrackId: string, effectType: string, name?: string) => string;
  addEffectClip: (trackId: string, clip: Omit<EffectClip, 'id' | 'trackId'>) => string;
  /** Atomic: creates effect track + clip in a single undo-able operation */
  addEffectTrackWithClip: (parentTrackId: string, effectType: string, clip: Omit<EffectClip, 'id' | 'trackId'>, trackName?: string) => { trackId: string; clipId: string };
  removeEffectClip: (clipId: string) => void;
  moveEffectClip: (clipId: string, newStartMs: number) => void;
  trimEffectClip: (clipId: string, side: 'left' | 'right', deltaMs: number) => void;
  updateEffectClipParams: (clipId: string, params: Record<string, number>) => void;
  toggleEffectClipBypass: (clipId: string) => void;

  // Effects (Phase 5)
  addTrackEffect: (trackId: string, effect: import('../types').EffectInstance) => void;
  removeTrackEffect: (trackId: string, effectId: string) => void;
  updateTrackEffect: (trackId: string, effectId: string, params: Record<string, number>) => void;
  toggleTrackEffectBypass: (trackId: string, effectId: string) => void;
  addMasterEffect: (effect: import('../types').EffectInstance) => void;
  removeMasterEffect: (effectId: string) => void;
  updateMasterEffect: (effectId: string, params: Record<string, number>) => void;
  toggleMasterEffectBypass: (effectId: string) => void;

  // Timeline view
  setZoom: (pxPerMs: number) => void;
  setScrollOffset: (ms: number) => void;
  selectClip: (clipId: string | null) => void;
  zoomToFit: (canvasWidth: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
}

function defaultProjectName(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}${pad(d.getHours())}${pad(d.getSeconds())}`;
}

export function createEmptyProject(name = defaultProjectName()): EditorProject {
  return {
    id: uid(),
    name,
    tracks: [],
    assets: [],
    durationMs: 0,
    fps: DEFAULT_FPS,
    projectAspect: 16 / 9,
    createdAt: now(),
    updatedAt: now(),
  };
}

function recalcDuration(tracks: Track[]): number {
  let max = 0;
  for (const track of tracks) {
    for (const clip of track.clips) {
      const end = clip.startMs + clip.durationMs;
      if (end > max) max = end;
    }
    if (track.effectClips) {
      for (const ec of track.effectClips) {
        const end = ec.startMs + ec.durationMs;
        if (end > max) max = end;
      }
    }
  }
  return max;
}


export const useEditorStore = create<EditorStore>(withUndo((set, get) => ({
  project: createEmptyProject(),
  timeline: {
    pxPerMs: INITIAL_PX_PER_MS,
    scrollOffsetMs: 0,
    selectedClipId: null,
  },

  // These are provided by withUndo middleware:
  _undo: [],
  _redo: [],
  _skipHistory: false,
  undo: () => {},
  redo: () => {},
  canUndo: () => false,
  canRedo: () => false,
  loadProject: () => {},

  setProject: (project) => set({ project }),
  resetProject: (name) => set({ project: createEmptyProject(name) }),

  addTrack: (type, name) => {
    const id = uid();
    set((s) => ({
      project: {
        ...s.project,
        tracks: [...s.project.tracks, { id, name: name ?? `${type} ${s.project.tracks.length + 1}`, type, clips: [], muted: false, locked: false, volume: 1, pan: 0, soloed: false }],
        updatedAt: now(),
      },
    }));
    return id;
  },

  removeTrack: (trackId) =>
    set((s) => {
      const tracks = s.project.tracks.filter((t) => t.id !== trackId);
      return { project: { ...s.project, tracks, durationMs: recalcDuration(tracks), updatedAt: now() } };
    }),

  toggleTrackMute: (trackId) =>
    set((s) => ({
      project: {
        ...s.project,
        tracks: s.project.tracks.map((t) => (t.id === trackId ? { ...t, muted: !t.muted } : t)),
        updatedAt: now(),
      },
    })),

  toggleTrackLock: (trackId) =>
    set((s) => ({
      project: {
        ...s.project,
        tracks: s.project.tracks.map((t) => (t.id === trackId ? { ...t, locked: !t.locked } : t)),
        updatedAt: now(),
      },
    })),

  addClip: (trackId, clipData) => {
    const id = uid();
    set((s) => {
      const tracks = s.project.tracks.map((t) => {
        if (t.id !== trackId) return t;
        return { ...t, clips: [...t.clips, { ...clipData, id, trackId }] };
      });
      return { project: { ...s.project, tracks, durationMs: recalcDuration(tracks), updatedAt: now() } };
    });
    return id;
  },

  removeClip: (clipId) =>
    set((s) => {
      const tracks = s.project.tracks.map((t) => ({
        ...t,
        clips: t.clips.filter((c) => c.id !== clipId),
      }));
      return { project: { ...s.project, tracks, durationMs: recalcDuration(tracks), updatedAt: now() } };
    }),

  moveClip: (clipId, newStartMs) =>
    set((s) => {
      const tracks = s.project.tracks.map((t) => ({
        ...t,
        clips: t.clips.map((c) => (c.id === clipId ? { ...c, startMs: Math.max(0, newStartMs) } : c)),
      }));
      return { project: { ...s.project, tracks, durationMs: recalcDuration(tracks), updatedAt: now() } };
    }),

  splitClip: (clipId, splitAtMs) =>
    set((s) => {
      const tracks = s.project.tracks.map((t) => {
        const clipIdx = t.clips.findIndex((c) => c.id === clipId);
        if (clipIdx === -1) return t;

        const clip = t.clips[clipIdx];
        const relativeMs = splitAtMs - clip.startMs;

        // Can't split if playhead is outside clip or too close to edges
        if (relativeMs <= 100 || relativeMs >= clip.durationMs - 100) return t;

        const leftClip: Clip = {
          ...clip,
          durationMs: relativeMs,
        };

        const rightClip: Clip = {
          id: uid(),
          assetId: clip.assetId,
          trackId: clip.trackId,
          startMs: splitAtMs,
          sourceOffsetMs: clip.sourceOffsetMs + relativeMs,
          durationMs: clip.durationMs - relativeMs,
        };

        const newClips = [...t.clips];
        newClips.splice(clipIdx, 1, leftClip, rightClip);
        return { ...t, clips: newClips };
      });

      return { project: { ...s.project, tracks, durationMs: recalcDuration(tracks), updatedAt: now() } };
    }),

  trimClip: (clipId, side, deltaMs) =>
    set((s) => {
      const tracks = s.project.tracks.map((t) => ({
        ...t,
        clips: t.clips.map((c) => {
          if (c.id !== clipId) return c;

          if (side === 'left') {
            const clampedDelta = Math.min(deltaMs, c.durationMs - 100);
            const safeDelta = Math.max(clampedDelta, -c.sourceOffsetMs);
            return {
              ...c,
              startMs: c.startMs + safeDelta,
              sourceOffsetMs: c.sourceOffsetMs + safeDelta,
              durationMs: c.durationMs - safeDelta,
            };
          } else {
            // right edge: positive delta = drag right = extend
            const newDuration = Math.max(100, c.durationMs + deltaMs);
            return { ...c, durationMs: newDuration };
          }
        }),
      }));
      return { project: { ...s.project, tracks, durationMs: recalcDuration(tracks), updatedAt: now() } };
    }),

  addAsset: (asset) =>
    set((s) => ({
      project: { ...s.project, assets: [...s.project.assets, asset], updatedAt: now() },
    })),

  updateAssetStatus: (assetId, status, progress, error) =>
    set((s) => ({
      project: {
        ...s.project,
        assets: s.project.assets.map((a) =>
          a.id === assetId ? { ...a, ingestStatus: status, ingestProgress: progress ?? a.ingestProgress, ingestError: error } : a,
        ),
        updatedAt: now(),
      },
    })),

  updateAsset: (assetId, updates) =>
    set((s) => ({
      project: {
        ...s.project,
        assets: s.project.assets.map((a) => (a.id === assetId ? { ...a, ...updates } : a)),
        updatedAt: now(),
      },
    })),

  removeAsset: (assetId) =>
    set((s) => ({
      project: {
        ...s.project,
        assets: s.project.assets.filter((a) => a.id !== assetId),
        updatedAt: now(),
      },
    })),

  setProjectAspect: (aspect) =>
    set((s) => ({
      project: { ...s.project, projectAspect: aspect, updatedAt: now() },
    })),

  // Clip fades (Phase 4)
  setClipFadeIn: (clipId, durationMs, type = 'linear') =>
    set((s) => ({
      project: {
        ...s.project,
        tracks: s.project.tracks.map((t) => ({
          ...t,
          clips: t.clips.map((c) => (c.id === clipId ? { ...c, fadeIn: { durationMs, type } } : c)),
        })),
        updatedAt: now(),
      },
    })),

  setClipFadeOut: (clipId, durationMs, type = 'linear') =>
    set((s) => ({
      project: {
        ...s.project,
        tracks: s.project.tracks.map((t) => ({
          ...t,
          clips: t.clips.map((c) => (c.id === clipId ? { ...c, fadeOut: { durationMs, type } } : c)),
        })),
        updatedAt: now(),
      },
    })),

  // Effect Clips (Region-based Event FX)
  addEffectTrack: (parentTrackId, effectType, name) => {
    const id = uid();
    set((s) => {
      const parentIdx = s.project.tracks.findIndex((t) => t.id === parentTrackId);
      const tracks = [...s.project.tracks];
      const newTrack: Track = {
        id,
        name: name ?? effectType.charAt(0).toUpperCase() + effectType.slice(1),
        type: 'effect',
        clips: [],
        muted: false,
        locked: false,
        volume: 1,
        pan: 0,
        soloed: false,
        effectClips: [],
        parentTrackId,
      };
      // Insert right below the parent audio track
      tracks.splice(parentIdx + 1, 0, newTrack);
      return { project: { ...s.project, tracks, updatedAt: now() } };
    });
    return id;
  },

  addEffectClip: (trackId, clipData) => {
    const id = uid();
    set((s) => {
      const tracks = s.project.tracks.map((t) => {
        if (t.id !== trackId) return t;
        return { ...t, effectClips: [...(t.effectClips ?? []), { ...clipData, id, trackId }] };
      });
      return { project: { ...s.project, tracks, durationMs: recalcDuration(tracks), updatedAt: now() } };
    });
    return id;
  },

  addEffectTrackWithClip: (parentTrackId, effectType, clipData, trackName) => {
    const trackId = uid();
    const clipId = uid();
    set((s) => {
      const parentIdx = s.project.tracks.findIndex((t) => t.id === parentTrackId);
      const tracks = [...s.project.tracks];
      const newTrack: Track = {
        id: trackId,
        name: trackName ?? effectType.charAt(0).toUpperCase() + effectType.slice(1),
        type: 'effect',
        clips: [],
        muted: false,
        locked: false,
        volume: 1,
        pan: 0,
        soloed: false,
        effectClips: [{ ...clipData, id: clipId, trackId }],
        parentTrackId,
      };
      tracks.splice(parentIdx + 1, 0, newTrack);
      return { project: { ...s.project, tracks, durationMs: recalcDuration(tracks), updatedAt: now() } };
    });
    return { trackId, clipId };
  },

  removeEffectClip: (clipId) =>
    set((s) => {
      let tracks = s.project.tracks.map((t) => {
        if (!t.effectClips) return t;
        return { ...t, effectClips: t.effectClips.filter((ec) => ec.id !== clipId) };
      });
      // Remove effect tracks with no clips left
      tracks = tracks.filter((t) => t.type !== 'effect' || (t.effectClips && t.effectClips.length > 0));
      return { project: { ...s.project, tracks, durationMs: recalcDuration(tracks), updatedAt: now() } };
    }),

  moveEffectClip: (clipId, newStartMs) =>
    set((s) => {
      const tracks = s.project.tracks.map((t) => {
        if (!t.effectClips) return t;
        return {
          ...t,
          effectClips: t.effectClips.map((ec) =>
            ec.id === clipId ? { ...ec, startMs: Math.max(0, newStartMs) } : ec,
          ),
        };
      });
      return { project: { ...s.project, tracks, durationMs: recalcDuration(tracks), updatedAt: now() } };
    }),

  trimEffectClip: (clipId, side, deltaMs) =>
    set((s) => {
      const tracks = s.project.tracks.map((t) => {
        if (!t.effectClips) return t;
        return {
          ...t,
          effectClips: t.effectClips.map((ec) => {
            if (ec.id !== clipId) return ec;
            if (side === 'left') {
              const clampedDelta = Math.min(deltaMs, ec.durationMs - 100);
              const safeDelta = Math.max(clampedDelta, -ec.startMs);
              return { ...ec, startMs: ec.startMs + safeDelta, durationMs: ec.durationMs - safeDelta };
            } else {
              return { ...ec, durationMs: Math.max(100, ec.durationMs + deltaMs) };
            }
          }),
        };
      });
      return { project: { ...s.project, tracks, durationMs: recalcDuration(tracks), updatedAt: now() } };
    }),

  updateEffectClipParams: (clipId, params) =>
    set((s) => ({
      project: {
        ...s.project,
        tracks: s.project.tracks.map((t) => {
          if (!t.effectClips) return t;
          return {
            ...t,
            effectClips: t.effectClips.map((ec) =>
              ec.id === clipId ? { ...ec, params: { ...ec.params, ...params } } : ec,
            ),
          };
        }),
        updatedAt: now(),
      },
    })),

  toggleEffectClipBypass: (clipId) =>
    set((s) => ({
      project: {
        ...s.project,
        tracks: s.project.tracks.map((t) => {
          if (!t.effectClips) return t;
          return {
            ...t,
            effectClips: t.effectClips.map((ec) =>
              ec.id === clipId ? { ...ec, bypassed: !ec.bypassed } : ec,
            ),
          };
        }),
        updatedAt: now(),
      },
    })),

  // Effects (Phase 5)
  addTrackEffect: (trackId, effect) =>
    set((s) => ({
      project: {
        ...s.project,
        tracks: s.project.tracks.map((t) => (t.id === trackId ? { ...t, effects: [...(t.effects ?? []), effect] } : t)),
        updatedAt: now(),
      },
    })),

  removeTrackEffect: (trackId, effectId) =>
    set((s) => ({
      project: {
        ...s.project,
        tracks: s.project.tracks.map((t) => (t.id === trackId ? { ...t, effects: (t.effects ?? []).filter((e) => e.id !== effectId) } : t)),
        updatedAt: now(),
      },
    })),

  updateTrackEffect: (trackId, effectId, params) =>
    set((s) => ({
      project: {
        ...s.project,
        tracks: s.project.tracks.map((t) => (t.id === trackId ? {
          ...t, effects: (t.effects ?? []).map((e) => (e.id === effectId ? { ...e, params: { ...e.params, ...params } } : e)),
        } : t)),
        updatedAt: now(),
      },
    })),

  toggleTrackEffectBypass: (trackId, effectId) =>
    set((s) => ({
      project: {
        ...s.project,
        tracks: s.project.tracks.map((t) => (t.id === trackId ? {
          ...t, effects: (t.effects ?? []).map((e) => (e.id === effectId ? { ...e, bypassed: !e.bypassed } : e)),
        } : t)),
        updatedAt: now(),
      },
    })),

  addMasterEffect: (effect) =>
    set((s) => ({
      project: {
        ...s.project,
        masterEffects: [...(s.project.masterEffects ?? []), effect],
        updatedAt: now(),
      },
    })),

  removeMasterEffect: (effectId) =>
    set((s) => ({
      project: {
        ...s.project,
        masterEffects: (s.project.masterEffects ?? []).filter((e) => e.id !== effectId),
        updatedAt: now(),
      },
    })),

  updateMasterEffect: (effectId, params) =>
    set((s) => ({
      project: {
        ...s.project,
        masterEffects: (s.project.masterEffects ?? []).map((e) => (e.id === effectId ? { ...e, params: { ...e.params, ...params } } : e)),
        updatedAt: now(),
      },
    })),

  toggleMasterEffectBypass: (effectId) =>
    set((s) => ({
      project: {
        ...s.project,
        masterEffects: (s.project.masterEffects ?? []).map((e) => (e.id === effectId ? { ...e, bypassed: !e.bypassed } : e)),
        updatedAt: now(),
      },
    })),

  // Per-track mixing (Phase 3)
  setTrackVolume: (trackId, volume) =>
    set((s) => ({
      project: {
        ...s.project,
        tracks: s.project.tracks.map((t) => (t.id === trackId ? { ...t, volume: Math.max(0, Math.min(1, volume)) } : t)),
        updatedAt: now(),
      },
    })),

  setTrackPan: (trackId, pan) =>
    set((s) => ({
      project: {
        ...s.project,
        tracks: s.project.tracks.map((t) => (t.id === trackId ? { ...t, pan: Math.max(-1, Math.min(1, pan)) } : t)),
        updatedAt: now(),
      },
    })),

  toggleTrackSolo: (trackId) =>
    set((s) => ({
      project: {
        ...s.project,
        tracks: s.project.tracks.map((t) => (t.id === trackId ? { ...t, soloed: !t.soloed } : t)),
        updatedAt: now(),
      },
    })),

  setZoom: (pxPerMs) => set((s) => ({
    timeline: { ...s.timeline, pxPerMs: Math.min(1.5, Math.max(0.005, pxPerMs)) },
  })),
  setScrollOffset: (ms) => set((s) => ({ timeline: { ...s.timeline, scrollOffsetMs: ms } })),
  selectClip: (clipId) => set((s) => ({ timeline: { ...s.timeline, selectedClipId: clipId } })),

  zoomIn: () => set((s) => ({ timeline: { ...s.timeline, pxPerMs: zoomInLevel(s.timeline.pxPerMs) } })),
  zoomOut: () => set((s) => ({ timeline: { ...s.timeline, pxPerMs: zoomOutLevel(s.timeline.pxPerMs) } })),

  zoomToFit: (canvasWidth) => set((s) => {
    const durationMs = s.project.durationMs;
    if (durationMs <= 0 || canvasWidth <= 0) return s;
    // Leave 24px padding (12px each side from CONTENT_PAD_LEFT + right margin)
    const usableWidth = canvasWidth - 24;
    const pxPerMs = Math.max(0.001, usableWidth / durationMs);
    return { timeline: { ...s.timeline, pxPerMs, scrollOffsetMs: 0 } };
  }),
})));
